import React, {Component} from "react"

import {H2, H4, Body, Label} from "@irati/ui/src/Typography"
import Link, {ButtonRouterLink, RouterLink} from "@irati/ui/src/Link"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import theme from "@irati/ui/src/global/theme"
import Flex from "@irati/ui/src/Flex"
import Spacing from "@irati/ui/src/Spacing"
import Checkbox from "@irati/ui/src/FormItem/Checkbox"
import {FormattedMessage} from "@irati/utils/lang"
import checklistService, {EVENT_DSYNC, DSyncChecklistType} from "@irati/admin/services/integrations/checklistService"

import {SETTINGS_LOGIN_INTEGRATION_DSYNC, VIEW_PEOPLE, VIEW_BUSINESS_UNITS} from "../../../../../routes/routeConfig"
import {InfoCard} from "../../styled"

interface Props {}

interface State {
  checklist: DSyncChecklistType[]
}

class ChecklistDirectorySync extends Component<Props, State> {
  state: State = {
    checklist: checklistService.getDsyncChecklist()
  }

  componentDidMount() {
    checklistService.addEventListener(EVENT_DSYNC, this.handleStateChecklist)
  }

  componentWillUnmount() {
    checklistService.removeEventListener(EVENT_DSYNC, this.handleStateChecklist)
  }

  handleStateChecklist = () => {
    this.setState({checklist: checklistService.getDsyncChecklist()})
  }

  setToLocalStorage = (value: DSyncChecklistType) => {
    checklistService.setDSyncChecklist(value)
  }

  getCheckboxState = (key: string): boolean => {
    return this.state.checklist.findIndex((item) => item === key) > -1
  }

  render() {
    return (
      <>
        <Icon icon={IconTypes.chevronLeft} color={theme.colors.primary2} marginRight={1} />
        <RouterLink primary to={SETTINGS_LOGIN_INTEGRATION_DSYNC}>
          <FormattedMessage id="admin.components.integration.directorySync.checklistPage.backLink" />
        </RouterLink>
        <H2 marginTop={1} marginBottom={1}>
          <FormattedMessage id="admin.components.integration.directorySync.checklistPage.title" />
        </H2>
        <Body marginBottom={0}>
          <FormattedMessage id="admin.components.integration.directorySync.checklistPage.text.paragraph1" />
        </Body>
        <Body marginBottom={4}>
          <FormattedMessage id="admin.components.integration.directorySync.checklistPage.text.paragraph2" />
        </Body>
        <H4 marginBottom={2}>
          <FormattedMessage id="admin.components.integration.directorySync.checklistPage.subtitle1" />
        </H4>
        <Flex marginBottom={2} marginLeft={-2}>
          <Checkbox
            name={DSyncChecklistType.CK_SETUP_DOCUMENTATION}
            checked={this.getCheckboxState(DSyncChecklistType.CK_SETUP_DOCUMENTATION)}
            onChange={() => {
              this.setToLocalStorage(DSyncChecklistType.CK_SETUP_DOCUMENTATION)
            }}>
            <Label large>
              <FormattedMessage
                id="admin.components.integration.directorySync.checklistPage.subtext1"
                values={{
                  documentation: (
                    <Link large cta href="https://help.perkbox.com/en/articles/5338585-link-your-people-directory" target="_blank">
                      <FormattedMessage id="admin.components.integration.directorySync.checklistPage.documentation" />
                    </Link>
                  )
                }}
              />
            </Label>
          </Checkbox>
        </Flex>
        <Flex marginLeft={-2}>
          <Checkbox
            name={DSyncChecklistType.CK_ADMIN_ACCESS}
            checked={this.getCheckboxState(DSyncChecklistType.CK_ADMIN_ACCESS)}
            onChange={() => {
              this.setToLocalStorage(DSyncChecklistType.CK_ADMIN_ACCESS)
            }}>
            <Label large>
              <FormattedMessage id="admin.components.integration.directorySync.checklistPage.subtext2" />
            </Label>
          </Checkbox>
        </Flex>
        <ButtonRouterLink to={VIEW_PEOPLE} marginLeft={4} small>
          <FormattedMessage id="admin.components.integration.directorySync.checklistPage.buttonViewPeople" />
          <Icon icon={IconTypes.chevronRight} color={theme.colors.primary2} marginLeft={1} size={8} />
        </ButtonRouterLink>
        <H4 marginTop={8} marginBottom={2}>
          <FormattedMessage id="admin.components.integration.directorySync.checklistPage.subtitle2" />
        </H4>
        <Flex marginBottom={6} marginLeft={-2}>
          <Checkbox
            name={DSyncChecklistType.CK_ACTIVATION_EMAILS}
            checked={this.getCheckboxState(DSyncChecklistType.CK_ACTIVATION_EMAILS)}
            onChange={() => {
              this.setToLocalStorage(DSyncChecklistType.CK_ACTIVATION_EMAILS)
            }}>
            <Label large>
              <FormattedMessage
                id="admin.components.integration.directorySync.checklistPage.subtext3.paragraph1"
                values={{
                  immediately: (
                    <Label large weight="bold">
                      <FormattedMessage id="admin.components.integration.directorySync.checklistPage.subtext3.immediately" />
                    </Label>
                  )
                }}
              />
              <Spacing marginLeft={3.5}>
                <FormattedMessage id="admin.components.integration.directorySync.checklistPage.subtext3.paragraph2" />
              </Spacing>
              <Spacing />
              <Spacing marginLeft={3.5}>
                <FormattedMessage id="admin.components.integration.directorySync.checklistPage.subtext3.paragraph3" />
              </Spacing>
            </Label>
          </Checkbox>
        </Flex>
        <H4 marginBottom={2}>
          <FormattedMessage id="admin.components.integration.directorySync.checklistPage.subtitle3" />
        </H4>
        <Flex marginLeft={-2}>
          <Checkbox
            name={DSyncChecklistType.CK_PEOPLE_DATA}
            checked={this.getCheckboxState(DSyncChecklistType.CK_PEOPLE_DATA)}
            onChange={() => {
              this.setToLocalStorage(DSyncChecklistType.CK_PEOPLE_DATA)
            }}>
            <Label large>
              <FormattedMessage id="admin.components.integration.directorySync.checklistPage.subtext4.paragraph1" />
              <Spacing marginLeft={3.5}>
                <FormattedMessage id="admin.components.integration.directorySync.checklistPage.subtext4.paragraph2" />
              </Spacing>
            </Label>
          </Checkbox>
        </Flex>
        <InfoCard marginLeft={3} marginBottom={4} padding={2}>
          <Icon icon={IconTypes.circleAlert} color={theme.colors.primary2} marginRight={1} marginTop="-10px" size={40} />
          <Flex direction="column" gap={theme.spacing.two} marginLeft={0.5}>
            <Label large>
              <FormattedMessage id="admin.components.integration.directorySync.checklistPage.warning.text" />
            </Label>
            <div>
              <ButtonRouterLink to={VIEW_PEOPLE} small>
                <FormattedMessage id="admin.components.integration.directorySync.checklistPage.warning.button" />
                <Icon icon={IconTypes.chevronRight} color={theme.colors.primary2} marginLeft={1} size={8} />
              </ButtonRouterLink>
            </div>
          </Flex>
        </InfoCard>
        <Flex marginBottom={2} marginLeft={-2}>
          <Checkbox
            name={DSyncChecklistType.CK_PROVIDERS}
            checked={this.getCheckboxState(DSyncChecklistType.CK_PROVIDERS)}
            onChange={() => {
              this.setToLocalStorage(DSyncChecklistType.CK_PROVIDERS)
            }}>
            <Label large>
              <FormattedMessage id="admin.components.integration.directorySync.checklistPage.subtext5.paragraph1" />
              <Spacing marginLeft={3.5}>
                <FormattedMessage
                  id="admin.components.integration.directorySync.checklistPage.subtext5.paragraph2"
                  values={{
                    providers: (
                      <Label large weight="bold">
                        <FormattedMessage id="admin.components.integration.directorySync.checklistPage.subtext5.providers" />
                      </Label>
                    )
                  }}
                />
              </Spacing>
              <Spacing marginLeft={3.5}>
                <FormattedMessage id="admin.components.integration.directorySync.checklistPage.subtext5.paragraph3" />
              </Spacing>
            </Label>
          </Checkbox>
        </Flex>
        <Flex marginLeft={-2}>
          <Checkbox
            name={DSyncChecklistType.CK_REGIONS}
            checked={this.getCheckboxState(DSyncChecklistType.CK_REGIONS)}
            onChange={() => {
              this.setToLocalStorage(DSyncChecklistType.CK_REGIONS)
            }}>
            <Label large>
              <FormattedMessage id="admin.components.integration.directorySync.checklistPage.subtext6.paragraph1" />
              <Spacing marginLeft={3.5}>
                <FormattedMessage id="admin.components.integration.directorySync.checklistPage.subtext6.paragraph4" />
              </Spacing>
              <Spacing marginLeft={3.5}>
                <FormattedMessage id="admin.components.integration.directorySync.checklistPage.subtext6.paragraph5" />
              </Spacing>
            </Label>
          </Checkbox>
        </Flex>
        <ButtonRouterLink to={VIEW_BUSINESS_UNITS} marginLeft={4} small>
          <FormattedMessage id="admin.components.integration.directorySync.checklistPage.buttonViewCountries" />
          <Icon icon={IconTypes.chevronRight} color={theme.colors.primary2} marginLeft={1} size={8} />
        </ButtonRouterLink>
      </>
    )
  }
}

export default ChecklistDirectorySync
